import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { BarChart } from '@mui/x-charts/BarChart';
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import api, { API_URL } from "../../api";

const monthToNumber = {
  January: 1, February: 2, March: 3, April: 4, May: 5, June: 6,
  July: 7, August: 8, September: 9, October: 10, November: 11, December: 12,
  YTD: 'ytd'
};

function SinglePageBarChart({ hospitalId, hospitalName, selectedMonth, selectedYear }) {

  const [monthData, setMonthData] = useState([]);
  const [measures, setMeasures] = useState([]);
  const [measureBy, setMeasureBy] = useState('');
  const [loading, setLoading] = useState(false);

  // Memoize expensive calculations
  const hospitalIdMap = useMemo(() => ({
    'COH-HO-1': 0,  // Carnegie
    'COH-HO-2': 1,  // Mangum
    'COH-HO-3': 2,  // Pawhuska
    'COH-HO-4': 3,  // Prague
    'COH-HO-5': 4,  // Seiling
  }), []);
  

  // Fetch performance measures using the exact same logic as dashboard
  useEffect(() => {
    const fetchPerformanceMeasures = async () => {
      setLoading(true);
      try {
        const yearParam = selectedYear;


        if (selectedMonth === 'YTD') {
          const currentYear = new Date().getFullYear();
          const currentMonth = new Date().getMonth() + 1;
          const endMonth = (Number(yearParam) === currentYear) ? currentMonth : 12;
          let allMeasures = [];

          for (let month = 1; month <= endMonth; month++) {
            try {
              const response = await api.get(`${API_URL}/dashboard/measures_table_data/`, {
                params: {
                  year: yearParam,
                  month: month
                }
              });
              if (response.status === 200 && response.data && Array.isArray(response.data.measures)) {
                allMeasures.push(...response.data.measures);
              }
            } catch (monthError) {
              console.warn(`Error fetching measures for month ${month}:`, monthError);
            }
          }

          // console.log('All measures data for YTD:', allMeasures);

          // Process measures data like dashboard does
          const measureMap = {};
          allMeasures.forEach(measure => {
            const key = `${measure.name}|${measure.category}`;
            if (!measureMap[key]) {
              measureMap[key] = { ...measure, hospitals: (measure.hospitals || []).map(h => ({ ...h })) };
            } else {
              (measure.hospitals || []).forEach((h, idx) => {
                if (measureMap[key].hospitals[idx]) {
                  measureMap[key].hospitals[idx].value = (Number(measureMap[key].hospitals[idx].value) || 0) + (Number(h.value) || 0);
                }
              });
            }
          });

          const uniqueMeasures = new Set(Object.values(measureMap).map(m => m.name));
          const measuresList = [...uniqueMeasures];
          console.log('Available measures for YTD:', measuresList);

          setMeasures(measuresList);
          setMeasureBy(prev => (prev && measuresList.includes(prev) ? prev : measuresList[0] || ''));

        } else {
          const monthParam = monthToNumber[selectedMonth] || 1;
          const response = await api.get(`${API_URL}/dashboard/measures_table_data/`, {
            params: {
              year: yearParam,
              month: monthParam
            }
          });

          if (response.status === 200 && response.data && Array.isArray(response.data.measures)) {
  
              // Process measures data like dashboard does for single month
              const measureMap = {};
              response.data.measures.forEach(measure => {
                const key = `${measure.name}|${measure.category}`;
                if (!measureMap[key]) {
                  measureMap[key] = { ...measure, hospitals: (measure.hospitals || []).map(h => ({ ...h })) };
                } else {
                  (measure.hospitals || []).forEach((h, idx) => {
                    if (measureMap[key].hospitals[idx]) {
                      measureMap[key].hospitals[idx].value = (Number(measureMap[key].hospitals[idx].value) || 0) + (Number(h.value) || 0);
                    }
                  });
                }
              });
  
              const uniqueMeasures = new Set(Object.values(measureMap).map(m => m.name));
              const measuresList = [...uniqueMeasures];
              console.log('Available measures for single month:', measuresList);
  
              setMeasures(measuresList);
              setMeasureBy(prev => (prev && measuresList.includes(prev) ? prev : measuresList[0] || ''));
          } else {
            console.log('No measures data found for single month');
            setMeasures([]);
            setMeasureBy('');
          }
        }

      } catch (error) {
        console.error('Error fetching performance measures:', error);
        setMeasures([]);
        setMeasureBy('');
      } finally {
        setLoading(false);
      }
    };

    if (selectedYear && selectedMonth) {
      fetchPerformanceMeasures();
    }
  }, [selectedYear, selectedMonth]);


  // Fetch chart data for the selected measure - using exact same logic as dashboard
  const fetchMeasuresChartData = async () => {
    if (!measureBy) return;

    try {
      setLoading(true);
      const encodedMeasure = encodeURIComponent(measureBy);

      console.log('Fetching chart data for measure:', measureBy, 'year:', selectedYear);

      const response = await api.get(
        `${API_URL}/dashboard/measures_chart_data/${selectedYear}/${encodedMeasure}/`
      );

      console.log('Chart data response:', response.data);

      if (response.status === 200 && response.data) {
        // Process data exactly like dashboard does
        const processedData = response.data
          .map((data) =>
            data.measures
              .map((measure) =>
                measure.hospitals.map((hospital) => ({
                  label: hospital.name,
                  hospitalId: hospital.id,
                  data: hospital.months.map(
                    (monthData) => Object.values(monthData)[0] || 0
                  ),
                }))
              )
              .flat()
          )
          .flat();

        console.log('Processed chart data for', processedData.length, 'hospitals');
        console.log('Looking for hospital ID:', hospitalId);

        // Use hospital ID to index mapping for direct access
        console.log('Using hospital ID for indexing:', hospitalId);

        // Get the index for the hospital ID from memoized map
        const hospitalIndex = hospitalIdMap[hospitalId];

        let hospitalData = null;
        if (hospitalIndex !== undefined && processedData[hospitalIndex]) {
          hospitalData = processedData[hospitalIndex];
          console.log('Found hospital data by ID mapping:', hospitalData);
        } else {
          console.log('Hospital ID not found in mapping, using first available hospital as fallback');
          hospitalData = processedData[0];
        }

        console.log('Selected hospital data:', hospitalData);

        console.log('Found hospital data:', hospitalData);

        if (hospitalData) {

          // Process monthly data for the specific hospital
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                             'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

          const monthlyData = hospitalData.data.map((value, index) => ({
            month: monthNames[index],
            value: Number(value) || 0
          }));

          // Filter data based on selected month
          const filteredData = selectedMonth === 'YTD'
            ? monthlyData
            : monthlyData.slice(0, monthToNumber[selectedMonth] || 12);
          setMonthData(filteredData);
        } else {
          console.log('No data found for hospital:', hospitalId);
          setMonthData([]);
        }
      } else {
        console.log('No chart data response');
        setMonthData([]);
      }
    } catch (error) {
      console.error('Error fetching chart data:', error);
      setMonthData([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch chart data when measure changes - with debouncing for performance
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchMeasuresChartData();
    }, 150); // Small delay to prevent rapid API calls

    return () => clearTimeout(timeoutId);
  }, [measureBy, hospitalId, selectedYear, selectedMonth]);


  const handleMeasureBy = (value) => {
    setMeasureBy(value);
  };

  if (loading) {
    return (
      <div className="bar-chart">
        <div className="loading">Loading chart data...</div>
      </div>
    );
  }

  return (
    <>
      <div className="bar-chart">
        <div className="filter">
          <h2>Measure By</h2>
          <Select
            onChange={(e) => {
              handleMeasureBy(e.target.value);
            }}
            name="measureBy"
            id="measureBy"
            value={measureBy}
            defaultValue={measureBy}
            disabled={loading}
          >
            {
              measures.map((measure, index) => (
                <MenuItem key={index} value={measure}>
                  {measure}
                </MenuItem>
              ))
            }
          </Select>
        </div>

        {monthData.length > 0 ? (
          <BarChart
            dataset={monthData}
            xAxis={[{ scaleType: 'band', dataKey: 'month', label: "Months" }]}
            series={[{ dataKey: 'value', baseValue: 0 }]}
            height={300}
            colors={['#07aeef']}
            borderRadius={7}
            slotProps={{
              bar: {
                highlightScope: { faded: 'global', highlighted: 'item' },
                highlighted: { fill: '#07AEEF !important' },
                hovered: { fill: '#07AEEF !important' },
                faded: { fill: '#F4FCFF' },
              },
            }}
          />
        ) : (
          <div className="no-data">No data available.</div>
        )}
      </div>
    </>
  );
}

export default React.memo(SinglePageBarChart);
